import { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import youtubeIcon from '../../../assets/icons/youtube-icon.svg';
import { UPLOADS_BASE } from '../../../api/config';
import { resolveIntroductionVideo } from '../../../features/admin/utils/youtubeHelpers';
import './VideoCard.css';

export const VideoCard = ({
  thumbnail,
  duration,
  date,
  title,
  description,
  authorAvatar,
  authorName,
  authorRole,
  hasPlay,
  mediaLink,
}) => {
  const [playing, setPlaying] = useState(false);
  const canPlay = hasPlay && Boolean(mediaLink);
  const resolved = resolveIntroductionVideo(mediaLink, UPLOADS_BASE);

  useEffect(() => {
    setPlaying(false);
  }, [mediaLink]);

  const handlePlay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (canPlay && resolved) setPlaying(true);
  };

  const handleStop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPlaying(false);
  };

  const renderThumbContent = () => {
    if (playing && resolved) {
      if (resolved.kind === 'file') {
        return (
          <video
            controls
            autoPlay
            className="video-card__player"
            src={resolved.src}
            preload="metadata"
            playsInline
          >
            <track kind="captions" />
          </video>
        );
      }

      return (
        <iframe
          title={title}
          className="video-card__player"
          src={`${resolved.embedUrl}?autoplay=1`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      );
    }

    return (
      <>
        <img src={thumbnail} alt={title} className="video-card__img" />
        {canPlay && (
          <button
            type="button"
            className="video-card__play"
            aria-label={`Play ${title}`}
            onClick={handlePlay}
          >
            <img src={youtubeIcon} alt="" className="video-card__play-icon" />
          </button>
        )}
      </>
    );
  };

  return (
    <Card className={`video-card${playing ? ' video-card--playing' : ''}`}>
      <div
        className={`video-card__thumb${canPlay && !playing ? ' video-card__thumb--playable' : ''}${playing ? ' video-card__thumb--active' : ''}`}
        onClick={canPlay && !playing ? handlePlay : undefined}
        onKeyDown={canPlay && !playing ? (e) => { if (e.key === 'Enter' || e.key === ' ') handlePlay(e); } : undefined}
        role={canPlay && !playing ? 'button' : undefined}
        tabIndex={canPlay && !playing ? 0 : undefined}
        aria-label={canPlay && !playing ? `Play ${title}` : undefined}
      >
        {renderThumbContent()}
        {playing && (
          <button
            type="button"
            className="video-card__close"
            aria-label="Close video"
            onClick={handleStop}
          >
            ×
          </button>
        )}
      </div>

      <Card.Body className="video-card__body">
        <div className="video-card__meta">
          <span className="video-card__meta-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
            {duration}
          </span>
          <span className="video-card__meta-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {date}
          </span>
        </div>

        <Card.Title className="video-card__title">{title}</Card.Title>
        <Card.Text className="video-card__desc">{description}</Card.Text>

        <div className="video-card__author">
          <img src={authorAvatar} alt={authorName} className="video-card__avatar" />
          <div>
            <p className="video-card__author-name">{authorName}</p>
            <p className="video-card__author-role">{authorRole}</p>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};
